// frontend/src/pages/studentlogin.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './studentlogin.css';
import { API_BASE } from '../services/api.js';

// Helper: safely notify Android app of login event.
// Does nothing if not running inside the Edunol Android app.
function notifyAndroidLogin(token, student, userData) {
  try {
    if (window.Android && typeof window.Android.onLogin === 'function') {
      window.Android.onLogin(
        token,
        JSON.stringify(student),
        JSON.stringify(userData)
      );
    }
  } catch (e) {
    console.warn('Android bridge onLogin not available:', e);
  }
}

function StudentLogin() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '' // for new registrations
  });
  const [isRegistering, setIsRegistering] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Password reset states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetStep, setResetStep] = useState('email'); // 'email', 'token', 'success'
  const [resetData, setResetData] = useState({
    email: '',
    token: '',
    newPassword: '',
    confirmPassword: ''
  });

  const navigate = useNavigate();

  // If already logged in, redirect to dashboard
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('userData');
    console.log('Login page loaded, token:', token);
    if (token && userData) {
      console.log('Token found, redirecting to dashboard');
      navigate('/dashboard');
    }
  }, [navigate]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Save to localStorage (React uses this)
        localStorage.setItem('userData', JSON.stringify(data));
        localStorage.setItem('student', JSON.stringify(data.student));
        localStorage.setItem('token', data.token || 'mock-token');

        // Notify Android to save to SharedPreferences
        notifyAndroidLogin(data.token, data.student, data);

        navigate('/dashboard');
      } else {
        setError(data.message || 'Login failed. Please check your credentials.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!formData.name.trim()) {
      setError('Please enter your name');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Save to localStorage (React uses this)
        localStorage.setItem('userData', JSON.stringify(data));
        localStorage.setItem('student', JSON.stringify(data.student));
        localStorage.setItem('token', data.token || 'mock-token');

        // Notify Android to save to SharedPreferences
        notifyAndroidLogin(data.token, data.student, data);

        navigate('/dashboard');
      } else {
        setError(data.message || 'Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle forgot password request
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: resetData.email
        })
      });

      const data = await response.json();

      if (response.ok) {
        setResetStep('token');
        if (data.debug_token) {
          alert(`Debug: Reset token is ${data.debug_token}`);
        }
      } else {
        setError(data.detail || 'Error sending reset request');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle password reset confirmation
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (resetData.newPassword !== resetData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (resetData.newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: resetData.token,
          new_password: resetData.newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setResetStep('success');
      } else {
        setError(data.detail || 'Error resetting password');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetDataChange = (e) => {
    setResetData({
      ...resetData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const resetForgotPassword = () => {
    setShowForgotPassword(false);
    setResetStep('email');
    setResetData({
      email: '',
      token: '',
      newPassword: '',
      confirmPassword: ''
    });
    setError('');
  };

  const handleGoogleLogin = async (googleToken) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/auth/google-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: googleToken })
      });

      const data = await response.json();

      if (response.ok) {
        // Save to localStorage (React uses this)
        localStorage.setItem('userData', JSON.stringify(data));
        localStorage.setItem('student', JSON.stringify(data.student));
        localStorage.setItem('token', data.token || 'mock-token');

        // Notify Android to save to SharedPreferences
        notifyAndroidLogin(data.token, data.student, data);

        navigate('/dashboard');
      } else {
        setError(data.detail || 'Google login failed. Please try again.');
      }
    } catch (error) {
      console.error('Google login error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initializeGoogleSignIn = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: "616237404050-m9vj4jq4calf8hqa82gum9i1tmrseg9n.apps.googleusercontent.com",
          callback: (response) => handleGoogleLogin(response.credential)
        });
        window.google.accounts.id.renderButton(
          document.getElementById("google-signin-btn"),
          {
            theme: "outline",
            size: "large",
            text: "signin_with",
            width: "360"
          }
        );
      } else {
        setTimeout(initializeGoogleSignIn, 100);
      }
    };
    initializeGoogleSignIn();
  }, []);

  // NEW: Render forgot password modal
  const renderForgotPasswordModal = () => {
    if (!showForgotPassword) return null;

    return (
      <div className="forgot-password-overlay">
        <div className="forgot-password-modal">
          <div className="modal-header">
            <h2>Reset Password</h2>
            <button 
              className="close-btn"
              onClick={resetForgotPassword}
            >
              ✕
            </button>
          </div>

          {error && (
            <div className="error-message">
              <span>⚠️ {error}</span>
            </div>
          )}

          {resetStep === 'email' && (
            <form onSubmit={handleForgotPassword}>
              <div className="form-group">
                <label htmlFor="reset-email">Email Address</label>
                <input
                  type="email"
                  id="reset-email"
                  name="email"
                  value={resetData.email}
                  onChange={handleResetDataChange}
                  placeholder="Enter your email address"
                  required
                />
              </div>
              <button 
                type="submit" 
                className="reset-btn"
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          )}

          {resetStep === 'token' && (
            <div>
              <p className="reset-info">
                A reset token has been generated. In production, this would be sent to your email.
              </p>
              <form onSubmit={handleResetPassword}>
                <div className="form-group">
                  <label htmlFor="reset-token">Reset Token</label>
                  <input
                    type="text"
                    id="reset-token"
                    name="token"
                    value={resetData.token}
                    onChange={handleResetDataChange}
                    placeholder="Enter the reset token"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="new-password">New Password</label>
                  <input
                    type="password"
                    id="new-password"
                    name="newPassword"
                    value={resetData.newPassword}
                    onChange={handleResetDataChange}
                    placeholder="Enter new password"
                    required
                    minLength="6"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="confirm-password">Confirm Password</label>
                  <input
                    type="password"
                    id="confirm-password"
                    name="confirmPassword"
                    value={resetData.confirmPassword}
                    onChange={handleResetDataChange}
                    placeholder="Confirm new password"
                    required
                    minLength="6"
                  />
                </div>
                <button 
                  type="submit" 
                  className="reset-btn"
                  disabled={loading}
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            </div>
          )}

          {resetStep === 'success' && (
            <div className="reset-success">
              <div className="success-icon">✅</div>
              <h3>Password Reset Successful!</h3>
              <p>Your password has been reset successfully. You can now login with your new password.</p>
              <button 
                className="reset-btn"
                onClick={resetForgotPassword}
              >
                Back to Login
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="login-card">
          <div className="login-header">
            <h1>Smart AI Tutor</h1>
            <p>Your personalized learning companion</p>
          </div>

          {error && (
            <div className="error-message">
              <span>⚠️ {error}</span>
            </div>
          )}

          <form onSubmit={isRegistering ? handleRegister : handleLogin}>
            {isRegistering && (
              <div className="form-group">
                <label htmlFor="name">Full Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  required={isRegistering}
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your Gmail address"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">
                {isRegistering ? 'Create Password' : 'Password'}
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder={isRegistering ? 'Create a secure password' : 'Enter your password'}
                required
                minLength="6"
              />
            </div>

            

            <button 
              type="submit" 
              className="login-btn"
              disabled={loading}
            >
              {loading ? 'Please wait...' : (isRegistering ? 'Create Account' : 'Sign In')}
            </button>
          </form>

          <div className="login-footer">
            <p>
              {isRegistering ? 'Already have an account?' : 'New to Smart AI Tutor?'}
              <button 
                type="button" 
                className="toggle-btn"
                onClick={() => {
                  setIsRegistering(!isRegistering);
                  setError('');
                  setFormData({ email: '', password: '', name: '' });
                }}
              >
                {isRegistering ? 'Sign In' : 'Create Account'}
              </button>
            </p>
            
            {/* Add Forgot Password Link */}
            {!isRegistering && (
              <p>
                <button 
                  type="button" 
                  className="forgot-password-btn"
                  onClick={() => setShowForgotPassword(true)}
                >
                  Forgot Password?
                </button>
              </p>
            )}
          </div>

          <div className="google-login-section">
            <div className="divider">
              <span>or</span>
            </div>
            <div id="google-signin-btn"></div>
          </div>
          
          
          <div className="features-preview">
            <h3>What you'll get:</h3>
            <ul>
              <li>🎓 Interactive Teaching Mode</li>
              <li>❓ AI-Powered Doubt Resolution</li>
              <li>📊 Adaptive Quizzes</li>
              <li>📱 Mobile-Friendly Learning</li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* NEW: Forgot Password Modal */}
      {renderForgotPasswordModal()}
    </div>
  );
}

export default StudentLogin;